import RenderManager from "../rendering/render-manager";
import AssetLibrary from "../assets/asset-library";
import createImage from "./create-image";
import SlideManager from "./slide-manager";
import { isSeamless } from "../rendering/cubemap-seams/detect";

const cache = {};
const sides = ["px", "nx", "py", "ny", "pz", "nz"];

function collectEnvLight({ path, queue }) {
    queue.addItem(0, {
        data: { path, name: "envLight" },
        type: "envLight"
    }, params => loadEnvLight(params.item.data.path));
}

function loadEnvLight(url) {
    if (!url) return new Promise(r => r(null));
    if (!Q3.browserCapabilities.supports.envLights.fullRes) {
        url = url
            .replace("https://coobo.s3.ap-south-1.amazonaws.com", "")
            .replace(".env", "_2.env")
            .replace("_1024.exr", "_512.exr");
    }

    if (cache[url]) {
        if (url.endsWith(".exr") && cache[url].intensity !== (SlideManager.currentSlide.envIntensity || 1)) {
            return new Promise(resolve => {
                parseBinaryEnv(cache[url].blob).then(data => {
                    cache[url] = data;
                    resolve(data);
                });
            });
        } else return Promise.resolve(cache[url]);
    }

    return new Promise(resolve => {
        let xhr = new XMLHttpRequest();
        xhr.open("GET", 'https://coobo-cdn.embibe.com/' + url, true);
        xhr.responseType = "blob";
        xhr.onload = function () {
            const blob = this.response;
            const fileReader = new FileReader();
            fileReader.onload = function (event) {
                parseBinaryEnv(event.target.result).then(data => {
                    cache[url] = data;
                    resolve(data);
                });
            };
            fileReader.readAsArrayBuffer(blob);
        };
        xhr.send();
    });
}

async function setEnvLight(envTexture) {
    if (!envTexture) return;

    let envMapCubeRes = 0;
    const allImages = [];
    let promises = null;
    const prevType = AssetLibrary.envLight.type;
    let newType = null;
    if (envTexture instanceof Array) {
        newType = AssetLibrary.envLight.type = "cube";
        const envMap = AssetLibrary.envLight.cubeMap;
        envMap.maxMipLevel = 5;
        envMap.mipmaps = [];

        envTexture.forEach((mipmap, i) => {
            envMap.mipmaps[i] = [];

            promises = sides.map(side => {
                const image = createImg(mipmap[side]);

                envMap.mipmaps[i].push(image.image);

                allImages.push(image.image);

                return image.promise;
            });
        });
    } else {
        newType = AssetLibrary.envLight.type = "erc";
        AssetLibrary.envLight.ercMap = envTexture.texture;
        AssetLibrary.envLight.resolution = envTexture.resolution;

        envMapCubeRes = envTexture.resolution;
    }

    await Promise
        .all(promises || [Promise.resolve()])
        .then(function () {
            const activeMap = AssetLibrary.envLight.activeMap;
            const typeChanged = newType !== prevType;

            if (typeChanged || newType === "erc") {
                RenderManager.root.traverse(x => {
                    if (x.material) {
                        (x.material instanceof Array ? x.material : [x.material]).forEach(material => {
                            material.envMap = activeMap;
                            if (typeChanged) material.needsUpdate = true;
                        });
                    }
                });
            }

            for (const image of allImages) {
                envMapCubeRes = Math.max(envMapCubeRes, image.naturalWidth, image.naturalHeight);
            }
            AssetLibrary.envLight.resolution = envMapCubeRes;

            RenderManager.root.traverse(x => {
                if (x.material) {
                    (x.material instanceof Array ? x.material : [x.material]).forEach(material => {
                        material.envMapCubeRes = envMapCubeRes;
                    });
                }
            });

            activeMap.needsUpdate = true;
            RenderManager.activeCamera.update = true;
        });
}

function initEnvLight() {
    return new Promise(resolve => {
        const envLightImage = createImage("rgba(30,30,30,255)");

        const cube = new THREE.CubeTexture(new Array(6).fill(envLightImage));

        cube.encoding = THREE.RGBM16Encoding;

        envLightImage.isReadyPromise.then(() => cube.needsUpdate = true);

        cube.generateMipmaps = false;
        cube.version = 1;

        resolve(cube);
    });
}

export { loadEnvLight, setEnvLight, initEnvLight, collectEnvLight };

function base64toBlob(base64Data, contentType) {
    contentType = contentType || '';

    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
        const begin = sliceIndex * sliceSize;
        const end = Math.min(begin + sliceSize, bytesLength);
        const bytes = new Array(end - begin);

        for (let offset = begin, i = 0; offset < end; ++i, ++offset)
            bytes[i] = byteCharacters[offset].charCodeAt(0);

        byteArrays[sliceIndex] = new Uint8Array(bytes);
    }

    return new Blob(byteArrays, { type: contentType });
}

function createImg(base64) {
    const image = new Image();
    const promise = new Promise(async resolve => {
        const blob = base64toBlob(base64, "image/png");
        const url = URL.createObjectURL(blob);

        image.onload = e => {
            // console.log("created image");
            resolve(e);
        }
        image.src = url;
    });

    return { image: image, promise: promise };
}

function base64ArrayBuffer(arrayBuffer) {
    let base64 = ''
    let encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

    let bytes = new Uint8Array(arrayBuffer)
    let byteLength = bytes.byteLength
    let byteRemainder = byteLength % 3
    let mainLength = byteLength - byteRemainder

    let a, b, c, d
    let chunk

    // Main loop deals with bytes in chunks of 3
    for (let i = 0; i < mainLength; i = i + 3) {
        // Combine the three bytes into a single integer
        chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2]

        // Use bitmasks to extract 6-bit segments from the triplet
        a = (chunk & 16515072) >> 18 // 16515072 = (2^6 - 1) << 18
        b = (chunk & 258048) >> 12 // 258048   = (2^6 - 1) << 12
        c = (chunk & 4032) >> 6 // 4032     = (2^6 - 1) << 6
        d = chunk & 63               // 63       = 2^6 - 1

        // Convert the raw binary segments to the appropriate ASCII encoding
        base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d]
    }

    // Deal with the remaining bytes and padding
    if (byteRemainder == 1) {
        chunk = bytes[mainLength]

        a = (chunk & 252) >> 2 // 252 = (2^6 - 1) << 2

        // Set the 4 least significant bits to zero
        b = (chunk & 3) << 4 // 3   = 2^2 - 1

        base64 += encodings[a] + encodings[b] + '=='
    } else if (byteRemainder == 2) {
        chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1]

        a = (chunk & 64512) >> 10 // 64512 = (2^6 - 1) << 10
        b = (chunk & 1008) >> 4 // 1008  = (2^6 - 1) << 4

        // Set the 2 least significant bits to zero
        c = (chunk & 15) << 2 // 15    = 2^4 - 1

        base64 += encodings[a] + encodings[b] + encodings[c] + '='
    }

    return base64
}

function readBuffer(buffer, ref) {
    const size = (buffer[ref.pos++] << 16) + (buffer[ref.pos++] << 8) + (buffer[ref.pos++]);
    const base64String = base64ArrayBuffer(buffer.slice(ref.pos, ref.pos + size));

    ref.pos += size;

    return base64String;
}

const awaitLibEXR = (function () {
    let promise;
    return function () {
        return promise = (promise || new Promise(resolve => {
            console.warn("Q3: OpenEXR loader not initialized. Begun downloading.");
            injectJS("/js/three.libs.modules.exr.js").then(function () {
                console.info("Q3: OpenEXR loader downloaded.");
                resolve(new THREE.EXRLoader());
            });
        }));
    };
})();

function parseBinaryEnv(blob) {
    return new Promise((resolve, reject) => {
        const arrayBuffer = new Uint8Array(blob);

        const isEXR = arrayBuffer[0] === 0x76 && arrayBuffer[1] === 0x2f && arrayBuffer[2] === 0x31 && arrayBuffer[3] === 0x01;

        if (isEXR) {
            awaitLibEXR().then(async function (loader) {
                const { width, height, data, format, type } = loader.parse(blob);
                const texture = new THREE.DataTexture(data, width, height, format, type);
                texture.mapping = THREE.EquirectangularReflectionMapping;
                texture.needsUpdate = true;

                const levels = 8, start = 1 << (levels - 1);
                const canvas = document.createElement("canvas"), ctx = canvas.getContext("2d");
                const renderer = RenderManager.mainRenderer;
                const cmGen = new THREE.CubemapGenerator(renderer);
                const gl = renderer.getContext();
                const slideIndensityAbsolute = SlideManager.currentSlide.envIntensity || 1;
                const slideIndensityPerceived = mapToPerceived(slideIndensityAbsolute);

                const CubemapRendersWithSeams = !await isSeamless;

                function renderResolution(resolution, intensity) {
                    const cubeRenderTarget = cmGen.fromEquirectangular(texture, {
                        intensity,
                        resolution,
                        generateMipmaps: true,
                        minFilter: CubemapRendersWithSeams ? THREE.LinearMipMapNearestFilter : THREE.LinearMipMapLinearFilter,
                        magFilter: THREE.LinearFilter
                    });

                    const buffers = [];

                    canvas.width = canvas.height = resolution;

                    const imageData = ctx.getImageData(0, 0, resolution, resolution);

                    for (let i = 0; i < 6; i++) {
                        cubeRenderTarget.activeCubeFace = i;
                        renderer.setRenderTarget(cubeRenderTarget);
                        gl.readPixels(0, 0, resolution, resolution, gl.RGBA, gl.UNSIGNED_BYTE, imageData.data);

                        ctx.putImageData(imageData, 0, 0);

                        buffers.push(canvas.toDataURL("image/png"));
                    }

                    return buffers;
                }

                function convertToCubedata(buffers) {
                    return buffers.reduce((accum, value, i) => {
                        accum[sides[i]] = value;

                        return accum;
                    }, {});
                }

                function createCubeDataNoMipmaps() {
                    const intensity = slideIndensityPerceived, resolution = 256;
                    const cubeRenderTarget = cmGen.fromEquirectangular(texture, {
                        intensity,
                        resolution,
                        generateMipmaps: true,
                        minFilter: CubemapRendersWithSeams ? THREE.LinearMipMapNearestFilter : THREE.LinearMipMapLinearFilter,
                        magFilter: THREE.LinearFilter
                    });

                    const cubeRenderTexture = cubeRenderTarget.texture;
                    cubeRenderTexture.minFilter = CubemapRendersWithSeams ? THREE.LinearMipMapNearestFilter : THREE.LinearMipMapLinearFilter;
                    cubeRenderTexture.generateMipmaps = true;

                    return cubeRenderTexture;
                }

                function createCubeDataRender() {
                    const cubeData = [];

                    for (let i = 0; i < levels; i++) {
                        const intensity = Math.pow(Math.max(1, 1 + i), 1);
                        const buffers = renderResolution(start >> i, intensity);

                        const images = buffers.map(b64 => createImg(b64).image);
                        cubeData.push(images);
                    }

                    const cubeTexture = new THREE.CubeTexture(cubeData[0]);
                    cubeTexture.mipmaps = cubeData.slice(2);
                    cubeTexture.maxMipLevel = 5;

                    return cubeTexture;
                }

                async function createCubeDataResize() {
                    const level0 = renderResolution(start, 1);
                    const inBuffers = level0.map(x => createImg(x));

                    await Promise.all(inBuffers.map(x => x.promise));

                    const cubeData = [convertToCubedata(level0)];

                    for (let i = 1; i < levels; i++) {
                        const buffers = [];
                        const resolution = start >> i;

                        canvas.width = canvas.height = resolution;

                        for (let j = 0, len = inBuffers.length; j < len; j++) {
                            ctx.drawImage(inBuffers[j].image, 0, 0, resolution, resolution);
                            buffers.push(canvas.toDataURL("image/png"));
                        }

                        const data = convertToCubedata(buffers);

                        cubeData.push(data);
                    }

                    return cubeData;
                }

                const cubeData = await createCubeDataNoMipmaps();

                resolve({
                    intensity: slideIndensityPerceived,
                    blob,
                    texture: cubeData,
                    resolution: 256,
                });
            });
        } else if (arrayBuffer[0] === 0) {
            let pos = 1;

            const levels = arrayBuffer[pos++];
            const ref = { pos };
            const cubeData = [];

            for (let i = 0; i < levels; i++) {
                cubeData.push(sides.reduce((accum, side) => {
                    accum[side] = readBuffer(arrayBuffer, ref);

                    return accum;
                }, {}));
            }

            resolve(cubeData);
        }
        else {
            reject(new Error("Unsupported version of env light."));
        }
    });
}

function mapToPerceived(absolute) {
    const constant = 1;
    return Math.pow(absolute, 2.4) * constant;
    // const Y = absolute;
    // const L = Y <= (216 / 24389)
    //     ? Y * (24389 / 27)
    //     : Math.pow(Y, (1 / 3)) * 116 - 16;

    // return L / 100;
}